import React, { useEffect, useState } from "react";
import classnames from "classnames";
import Link from "next/link";

import getStrainReviews from "@/api/requests/getStrainReviews";
import { Action, Category } from "@/constants/events";
import { StrainReview } from "@/custom-types/Reviews";
import { Strain } from "@/custom-types/Strain";
import { useEventTracker } from "@/hooks/useEventTracker";

import StarRating from "@/components/botanic/StarRating";
import TrackImpression from "@/components/TrackImpression";

import ReviewContainer, {
  ReviewType,
} from "../Reviews/components/ReviewContainer";

const TAKE = 3;

const StrainReviews = ({
  className,
  strain,
}: {
  className?: string;
  strain: Strain;
}) => {
  const [reviews, setReviews] = useState<StrainReview[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [page, setPage] = useState(1);

  const { averageRating, reviewCount, slug, name } = strain;
  const { publishEvent } = useEventTracker();

  const fetchReviews = (page = 1) => {
    const skip = (page - 1) * TAKE;
    return getStrainReviews(slug, TAKE, skip, "upvotes_count:desc");
  };

  useEffect(() => {
    fetchReviews().then(({ data: reviews, metadata: { totalCount } }) => {
      setReviews(reviews);
      setTotalCount(totalCount);
    });
  }, []);

  const handleLoadMore = () => {
    fetchReviews(page + 1).then(({ data: newReviews }) => {
      setReviews([...reviews, ...newReviews]);
      setPage(page + 1);
    });
  };

  return (
    <div id="pdp-strain-reviews" className={classnames(className)}>
      <h2 className="mb-sm">
        {reviewCount} {name} reviews
      </h2>
      <StarRating
        rating={averageRating}
        starWidth={16}
        className="text-sm mb-md"
        ratingCount={reviewCount}
      />
      {!reviewCount && (
        <div className="mb-lg">Be the first to write a review.</div>
      )}
      <TrackImpression
        category="Reviews"
        action="impression"
        label="menu strain write a review CTA"
      >
        <Link
          className="button mb-xxl"
          href={`/strains/${slug}/reviews/write-review`}
          onClick={() =>
            publishEvent({
              action: Action.click,
              category: Category.reviews,
              label: "menu strain write a review CTA",
            })
          }
        >
          write a strain review
        </Link>
      </TrackImpression>
      {!!reviews.length && (
        <>
          {reviews.map((review) => (
            <ReviewContainer
              review={review}
              type={ReviewType.STRAIN}
              itemReviewed={name}
              key={review.id}
            />
          ))}
          {reviews.length < totalCount && (
            <button
              className="button button--secondary"
              data-testid="strain-reviews-load-more"
              onClick={handleLoadMore}
            >
              load more reviews
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default StrainReviews;
